import { TagCategoryPicker } from '@/components/words/tag-category-picker';
// Simplified tags cell for the bulk-add review row: a single thin, non-wrapping
// line of chips (no category grouping — unlike TagEditor on word-detail) with
// one "+" that always opens TagCategoryPicker in 'tabbed' mode. When the chips
// overflow the available width they fade out with a trailing "…"; the "+"
// button itself is never clipped.
import { CATEGORY_ROWS, NOYAU_CSS, RemovableTag } from '@/components/words/tag-editor';
import type { CategoryRowKey } from '@/components/words/tag-editor';
import type { WordTag } from '@/schemas/word';
import { useLayoutEffect, useRef, useState } from 'react';

interface BulkTagLineProps {
  tags: WordTag[];
  readOnly?: boolean;
  onChange: (tags: WordTag[]) => void;
}

/** The tabbed picker's starting tab: the first category with an existing tag, else the first tab. */
function initialCategoryFor(tags: WordTag[]): CategoryRowKey {
  const withCategory = tags.find((tag) => tag.tagCategory);
  return withCategory?.tagCategory ?? CATEGORY_ROWS[0]?.key ?? 'none';
}

export function BulkTagLine({ tags, readOnly = false, onChange }: BulkTagLineProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // The ResizeObserver only fires on the container's own box changing, not on
  // its content overflowing (adding a chip grows scrollWidth without resizing
  // the flex box) — re-checking on `tags` too is required, not redundant.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tags drives overflow re-check independently of the ResizeObserver
  useLayoutEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tags]);

  function handleRemove(label: string) {
    onChange(tags.filter((t) => t.label !== label));
  }

  return (
    <div className="bulk-tag-line">
      <div ref={chipsRef} className={`bulk-tag-line__chips${overflowing ? ' is-overflowing' : ''}`}>
        {tags.map((tag) =>
          readOnly ? (
            <span key={tag.label} className="words-tag">
              {tag.noyauKey && (
                <span className="words-tag__dot" style={{ background: NOYAU_CSS[tag.noyauKey] }} />
              )}
              {tag.label}
            </span>
          ) : (
            <RemovableTag key={tag.label} tag={tag} onRemove={handleRemove} />
          ),
        )}
      </div>

      {!readOnly && (
        <button
          type="button"
          className="words-tag-category__add"
          aria-label="Ajouter des tags"
          onClick={() => setPickerOpen(true)}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            add
          </span>
        </button>
      )}

      {pickerOpen && (
        <TagCategoryPicker
          wordTags={tags}
          initialCategory={initialCategoryFor(tags)}
          forceStyle="tabbed"
          onConfirm={(next) => {
            onChange(next);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
